import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  viewChild,
} from '@angular/core';
import { AssetDto, AssetVersionDto, GalleryDto } from '@nusign/picflow-backend-types';
import { browserRenderableExtensions, downloadSizes, GALLERY_DOWNLOAD_SIZE } from '../../../consts';
import { getDownloadUrl } from '../../../gallery/lib/download-asset';
import { convertGalleryDownloadSize } from '../../../lib/convert-gallery-download-size';
import { AppConfigService } from '../../../providers/config.service';
import { TranslateService } from '../../../services/translate.service';
import { ContextMenuComponent } from '../../../shared/components/context-menu/context-menu.component';
import { LoadingIndicatorComponent } from '../../../shared/components/loading-indicator/loading-indicator.component';
import { SvgComponent } from '../../../shared/components/svg/svg.component';
import { ButtonDirective } from '../../../shared/directives/button.directive';
import { AssetVersionUrlPipe } from '../../../shared/pipes/asset-version-url.pipe';
import { DownloadSizeSelectionComponent } from '../download-size-selection/download-size-selection.component';

@Component({
  selector: 'app-ios-download-context',
  templateUrl: './ios-download-context.component.html',
  styleUrls: ['./ios-download-context.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ContextMenuComponent,
    ButtonDirective,
    SvgComponent,
    LoadingIndicatorComponent,
    DownloadSizeSelectionComponent,
  ],
  providers: [AssetVersionUrlPipe],
})
export class IosDownloadContextComponent implements OnChanges {
  private config = inject(AppConfigService);
  private translate = inject(TranslateService);
  private check = inject(ChangeDetectorRef);
  readonly menu = viewChild<ContextMenuComponent>('menu');
  readonly sizeMenu = viewChild<ContextMenuComponent>('sizeMenu');

  @Input() previewWidth: number;
  @Input() galleryPermissions: GalleryDto['permissions'];
  @Input() asset: AssetDto;
  @Input() version: AssetVersionDto;
  @Input() gallery: GalleryDto;
  @Output() download = new EventEmitter<number>();
  width: number;
  assetSrc: string;
  sizeName: string;
  height: number;
  format: string;
  orientation: 'vertical' | 'horizontal';
  aspectRatio: number;
  downloadMode: 'long-press' | 'button';

  ngOnChanges() {
    const enabledSizes = convertGalleryDownloadSize(this.gallery.download_media_size);
    const data = this.asset.version_data.data;
    if (!data) return;
    this.orientation = data.width > data.height ? 'horizontal' : 'vertical';
    this.aspectRatio = data.height / data.width;

    const perferenceList = ['medium', 'small', 'large', 'original'];

    const sizeToUse = perferenceList.find(size => enabledSizes[size]);

    this.updateDownloadLongEdge(GALLERY_DOWNLOAD_SIZE[sizeToUse] || 0);
  }

  async updateDownloadLongEdge(size: number) {
    if (size) {
      if (this.aspectRatio > 1) {
        this.width = Math.round(size / this.aspectRatio);
        this.height = size;
      } else {
        this.width = size;
        this.height = Math.round(size * this.aspectRatio);
      }
    } else {
      this.width = 0;
      this.height = 0;
    }
    this.format = this.calcFormat();
    this.assetSrc = await this.calcAssetSrc();
    this.sizeName = await this.calcSizeName();

    const isSimpleAsset = browserRenderableExtensions.includes(this.extension().toLowerCase());
    this.downloadMode = isSimpleAsset ? 'long-press' : 'button';
    this.check.detectChanges();
  }

  private async calcAssetSrc() {
    const longEdge = Math.max(this.width, this.height);
    return getDownloadUrl(
      this.asset,
      this.version || this.asset.version_data,
      longEdge,
      this.config.assetsEndpoint,
      this.asset.watermark_enabled
    );
  }

  private async calcSizeName() {
    if (!this.width) return 'Original File';
    const longEdge = Math.max(this.width, this.height);
    const size = downloadSizes.find(size => size.size === longEdge);
    if (!size) return 'UNKNOWN';
    return this.translate.get(size.name);
  }

  public activeLongEdge() {
    if (this.width) {
      return Math.max(this.width, this.height);
    }
    return 0;
  }

  private extension() {
    return this.asset.version_data.original_key.split('.').pop().toUpperCase();
  }

  private calcFormat() {
    if (!this.width) return this.extension();
    return 'JPG';
  }
}
